import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, FileSpreadsheet, Filter, TrendingUp, Wrench, UserPlus, Save, X, Users, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/** Rekap Penagihan — tabel simpel dari work_orders.billing.
 *  Filter: date range + technician. Total auto sum. */
const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function RekapPenagihan() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);   // v84.58
  // v84.105 — Restore filter dari sessionStorage supaya balik dari Billing Detail
  // tetap di filter yg sama. Save otomatis setiap change.
  const _savedFilter = (() => {
    try { return JSON.parse(sessionStorage.getItem("rekap_penagihan_filter") || "{}"); }
    catch { return {}; }
  })();
  const [dateFrom, setDateFrom] = useState(_savedFilter.dateFrom || todayISO());
  const [dateTo, setDateTo]     = useState(_savedFilter.dateTo   || todayISO());
  const [techId, setTechId]     = useState(_savedFilter.techId   || "all");
  const [statusFilter, setStatusFilter] = useState(_savedFilter.statusFilter || "all");
  // v82 — Personel + assignments
  const [personel, setPersonel] = useState([]);
  const [assignments, setAssignments] = useState([]);   // {id, date, armada_id, personnel_ids}
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDate, setAssignDate] = useState(todayISO());
  const [assignPersonnelIds, setAssignPersonnelIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  // v83.2 — Manual Billing modal
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const _emptyItem = () => ({ name: "", qty: 1, price: 0 });
  const [manual, setManual] = useState({
    date: todayISO(),
    technician_id: "",
    customer_name: "",
    customer_phone: "",
    address: "",
    notes: "",
    items: [_emptyItem()],
    payment_method: "CASH",
    send_wa: true,
  });

  // v84.105 — Persist filter ke sessionStorage setiap ada perubahan filter
  useEffect(() => {
    try {
      sessionStorage.setItem("rekap_penagihan_filter",
        JSON.stringify({ dateFrom, dateTo, techId, statusFilter }));
    } catch { /* ignore */ }
  }, [dateFrom, dateTo, techId, statusFilter]);

  useEffect(() => {
    api.listTechs().then(setTechs).catch(() => setTechs([]));
    api.listPersonnel().then(setPersonel).catch(() => setPersonel([]));  }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.billingRecap({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        technician_id: techId !== "all" ? techId : undefined,
      });
      setData(r);
      // v82 — Load assignments untuk armada+range terpilih
      if (techId !== "all" && dateFrom && dateTo) {
        const a = await api.listArmadaAssignments({
          armada_id: techId, date_from: dateFrom, date_to: dateTo,
        });
        setAssignments(a.rows || []);
      } else {
        setAssignments([]);
      }
    } catch (e) {
      toast.error("Gagal muat rekap: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, techId]);

  // v82 — Saat modal Assign dibuka, prefill personnel_ids yg sudah ter-assign di tgl tsb
  useEffect(() => {
    if (!assignOpen) return;
    const existing = assignments.find(a => a.date === assignDate);
    setAssignPersonnelIds(existing?.personnel_ids || []);
  }, [assignOpen, assignDate, assignments]);

  async function saveAssignment() {
    if (!techId || techId === "all") { toast.error("Pilih armada dulu di filter"); return; }
    if (!assignDate) { toast.error("Pilih tanggal"); return; }
    setAssignSaving(true);
    try {
      await api.upsertArmadaAssignment({
        armada_id: techId,
        date: assignDate,
        personnel_ids: assignPersonnelIds,
      });
      toast.success(`Assignment ${assignDate} disimpan (${assignPersonnelIds.length} personel)`);
      setAssignOpen(false);
      // reload assignments
      const a = await api.listArmadaAssignments({
        armada_id: techId, date_from: dateFrom, date_to: dateTo,
      });
      setAssignments(a.rows || []);
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setAssignSaving(false); }
  }

  function togglePersonnelInAssign(pid) {
    setAssignPersonnelIds(ids =>
      ids.includes(pid) ? ids.filter(x => x !== pid) : [...ids, pid]
    );
  }

  // v83.2 — Manual Billing handlers
  const manualGrand = useMemo(() => {
    return manual.items.reduce(
      (a, it) => a + (Number(it.qty) || 0) * (Number(it.price) || 0), 0
    );
  }, [manual.items]);

  function updateItem(i, patch) {
    setManual(m => ({ ...m, items: m.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  }
  function addItem() { setManual(m => ({ ...m, items: [...m.items, _emptyItem()] })); }
  function removeItem(i) {
    setManual(m => ({ ...m, items: m.items.length > 1 ? m.items.filter((_, idx) => idx !== i) : m.items }));
  }
  function resetManual() {
    setManual({
      date: todayISO(), technician_id: "", customer_name: "", customer_phone: "",
      address: "", notes: "", items: [_emptyItem()], payment_method: "CASH", send_wa: true,
    });
  }

  async function saveManual() {
    if (!manual.technician_id) { toast.error("Pilih armada"); return; }
    if (!manual.customer_name.trim()) { toast.error("Nama customer wajib"); return; }
    const cleanItems = manual.items
      .filter(it => (it.name || "").trim())
      .map(it => ({
        name: it.name.trim(), qty: Number(it.qty) || 1, price: Number(it.price) || 0,
      }));
    if (cleanItems.length === 0) { toast.error("Minimal 1 item dgn nama"); return; }
    setManualSaving(true);
    try {
      const r = await api.createManualBilling({
        date: manual.date,
        technician_id: manual.technician_id,
        customer_name: manual.customer_name.trim(),
        customer_phone: (manual.customer_phone || "").trim() || null,
        address: (manual.address || "").trim(),
        notes: (manual.notes || "").trim(),
        items: cleanItems,
        payment_method: manual.payment_method,
        send_wa: !!manual.send_wa,
      });
      const rp = "Rp " + Number(r.grand_total || 0).toLocaleString("id-ID");
      let msg = `Billing disimpan (${rp})`;
      const wa = r.whatsapp;
      if (wa) {
        if (wa.ok) msg += wa.simulated ? " · WA (simulasi) OK" : " · Invoice WA terkirim";
        else msg += ` · WA gagal: ${wa.error || wa.skipped || "?"}`;
      }
      toast.success(msg);
      setManualOpen(false);
      resetManual();
      await load();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally { setManualSaving(false); }
  }

  function assignmentForRowDate(date) {
    return assignments.find(a => a.date === date);
  }
  function personnelNamesFromIds(ids) {
    return ids.map(id => personel.find(p => p.id === id)?.name).filter(Boolean);
  }
  function openAssignForDate(date) {
    setAssignDate(date);
    setAssignOpen(true);
  }

  function exportCsv() {
    if (!data?.rows?.length) { toast.warning("Tidak ada data"); return; }
    const header = ["Tanggal","Armada","Personel","Customer","Alamat","Total","Jasa Teknisi","Metode"];
    const rows = data.rows.map(r => {
      const asg = assignmentForRowDate(r.date);
      const names = asg ? personnelNamesFromIds(asg.personnel_ids).join(" + ") : "";
      return [
        r.date, r.technician_name, names, r.customer_name || "",
        (r.address || "").replace(/[\r\n,]/g, " "), r.grand_total,
        r.tech_commission || 0, r.payment_method,
      ];
    });
    const csv = [header, ...rows].map(r =>
      r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const url = URL.createObjectURL(new Blob([csv], {type:"text/csv"}));
    const a = document.createElement("a");
    a.href = url; a.download = `rekap-penagihan-${dateFrom}_${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  // v84.58 — Export Excel: fetch dari backend endpoint, download file .xlsx
  // dgn 2 tab (Rekap Penagihan + Komisi Teknisi). Filter mengikuti UI state.
  async function exportExcel() {
    if (!data?.rows?.length) { toast.warning("Tidak ada data"); return; }
    setExportingXlsx(true);
    try {
      const blob = await api.exportBillingRecapExcel({
        date_from: dateFrom, date_to: dateTo,
        ...(techId !== "all" ? { technician_id: techId } : {}),
      });
      // Nama file dari Content-Disposition kalau ada, fallback compute lokal
      const [y, m] = dateFrom.split("-");
      const bln = ["Januari","Februari","Maret","April","Mei","Juni","Juli",
                   "Agustus","September","Oktober","November","Desember"][Math.max(0, (parseInt(m)||1) - 1)];
      const fname = `Laporan_Keuangan_${bln}_${y}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel berhasil didownload");
    } catch (e) {
      toast.error("Gagal export: " + (e?.response?.data?.detail || e.message));
    } finally {
      setExportingXlsx(false);
    }
  }

  // v84.80 — Summary: split TRANSFER & QRIS jadi Paid vs Pending.
  //   Cutoff date: WO dgn tanggal < 2026-08-26 di-grandfather sbg PAID
  //   (data lama, sblm fitur payment_status jalan). Semua WO ≥ 2026-08-26
  //   pakai field `payment_status` yg sebenarnya (PAID/PENDING).
  const PAYMENT_STATUS_START_DATE = "2026-08-26";
  // v84.83 — Helper: apakah row dianggap PAID? (pre-cutoff grandfather PAID)
  const isRowPaid = (r) =>
    (r.date || "") < PAYMENT_STATUS_START_DATE ||
    (r.payment_status || "PENDING") === "PAID";

  // v84.83 — Rows setelah filter status chip (client-side)
  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (statusFilter === "all") return data.rows;
    if (statusFilter === "paid") return data.rows.filter(isRowPaid);
    if (statusFilter === "pending") return data.rows.filter(r => !isRowPaid(r));
    return data.rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, statusFilter]);

  // v84.83 — Counter chip filter (jumlah per status)
  const statusCounts = useMemo(() => {
    if (!data?.rows) return { all: 0, paid: 0, pending: 0 };
    let paid = 0, pending = 0;
    data.rows.forEach(r => { isRowPaid(r) ? paid++ : pending++; });
    return { all: data.rows.length, paid, pending };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const summary = useMemo(() => {
    if (!data?.rows) return null;
    const byMethod = {};
    data.rows.forEach(r => {
      const m = r.payment_method || "CASH";
      if (!byMethod[m]) byMethod[m] = { total: 0, paid: 0, pending: 0 };
      byMethod[m].total += r.grand_total;
      if (isRowPaid(r)) byMethod[m].paid += r.grand_total;
      else byMethod[m].pending += r.grand_total;
    });
    return byMethod;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const armadaLocked = techId !== "all";
  const armadaName = armadaLocked ? (techs.find(t => t.id === techId)?.name || "") : "";

  return (
    <div className="space-y-4" data-testid="rekap-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Rekap Penagihan</h1>
          <p className="text-sm text-zinc-500 mt-1">Ringkasan invoice yang sudah disubmit teknisi via form billing publik.</p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={exportExcel} disabled={!data?.rows?.length || exportingXlsx}
          className="h-9 rounded-sm gap-1.5 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10"
          data-testid="export-xlsx-btn" title="Download .xlsx dgn 2 tab: Rekap Penagihan + Komisi Teknisi">
          <FileSpreadsheet className={`w-3.5 h-3.5 ${exportingXlsx ? "animate-pulse" : ""}`} />
          {exportingXlsx ? "Menyusun…" : "Export Excel"}
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.rows?.length}
          className="h-9 rounded-sm gap-1.5" data-testid="export-csv-btn">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
        </Button>
        <Button size="sm" onClick={() => setManualOpen(true)}
          className="h-9 rounded-sm gap-1.5 bg-[#E65100] hover:bg-[#F57C00]"
          data-testid="open-manual-billing-btn">
          <Plus className="w-3.5 h-3.5" /> Billing Manual
        </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 rounded-sm bg-white p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
          <Filter className="w-3 h-3"/> Filter
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] text-zinc-500 block mb-1">Dari Tanggal</label>
            <Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              className="rounded-sm h-9 text-[12px]" data-testid="date-from-input"/>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 block mb-1">Sampai Tanggal</label>
            <Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              className="rounded-sm h-9 text-[12px]" data-testid="date-to-input"/>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 block mb-1">Armada</label>
            <select value={techId} onChange={e=>setTechId(e.target.value)}
              className="rounded-sm h-9 w-full border border-zinc-200 bg-white px-2 text-[12px]"
              data-testid="tech-filter-select">
              <option value="all">Semua Armada</option>
              {(techs || []).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-1">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
              className="h-9 rounded-sm gap-1 flex-1" data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`}/> Refresh
            </Button>
            <Button variant="outline" size="sm"
              onClick={()=>{ setDateFrom(daysAgoISO(6)); setDateTo(todayISO()); setTechId("all"); }}
              className="h-9 rounded-sm text-[11px]" data-testid="reset-filter-btn">
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* v82 — Assign Teknisi panel (aktif kalau armada terpilih) */}
      {armadaLocked && (
        <div className="border-2 border-emerald-300 bg-emerald-50/40 rounded-sm p-3" data-testid="assign-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-zinc-700">
              <Users className="w-3.5 h-3.5 inline mr-1 text-emerald-700" />
              <b>Assign Teknisi ke armada <span className="text-emerald-800">{armadaName}</span></b> —
              pilih tanggal & personel yg bertugas.
              {personel.length === 0 && (
                <span className="text-red-600 ml-2">⚠️ Belum ada personel — buka menu <b>Data Teknisi</b>.</span>
              )}
            </div>
            <Button size="sm" onClick={() => openAssignForDate(dateFrom)}
              disabled={personel.length === 0}
              className="h-8 rounded-sm gap-1.5 bg-emerald-700 hover:bg-emerald-800"
              data-testid="assign-btn">
              <UserPlus className="w-3.5 h-3.5" /> Assign Teknisi
            </Button>
          </div>
          {assignments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {assignments.map(a => {
                const names = personnelNamesFromIds(a.personnel_ids);
                return (
                  <button key={a.id}
                    onClick={() => openAssignForDate(a.date)}
                    className="border border-emerald-300 bg-white rounded-sm px-2 py-1 text-[10px] hover:bg-emerald-100 transition"
                    data-testid={`assignment-chip-${a.date}`}>
                    <span className="font-mono font-semibold">{a.date}</span>{" · "}
                    <span className="text-emerald-800">{names.length > 0 ? names.join(" + ") : "kosong"}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Assign modal (simple inline) */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setAssignOpen(false)}>
          <div className="bg-white rounded-sm border border-zinc-200 shadow-lg w-full max-w-md p-4"
            onClick={e => e.stopPropagation()}
            data-testid="assign-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm">Assign Personel ke Armada</div>
              <Button size="icon" variant="ghost" onClick={() => setAssignOpen(false)} className="h-6 w-6">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-3">
              <div className="text-[11px] text-zinc-600 border-l-2 border-emerald-400 pl-2">
                Armada: <b className="text-emerald-800">{armadaName}</b>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Tanggal</label>
                <Input type="date" value={assignDate} onChange={e => setAssignDate(e.target.value)}
                  className="h-9 rounded-sm text-[12px] font-mono"
                  data-testid="assign-date-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
                  Personel yang bertugas ({assignPersonnelIds.length} dipilih)
                </label>
                <div className="border border-zinc-200 rounded-sm max-h-48 overflow-y-auto">
                  {personel.filter(p => p.active).map(p => (
                    <label key={p.id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-emerald-50 cursor-pointer text-[12px] border-b border-zinc-100 last:border-b-0"
                      data-testid={`assign-personel-option-${p.id}`}>
                      <input type="checkbox"
                        checked={assignPersonnelIds.includes(p.id)}
                        onChange={() => togglePersonnelInAssign(p.id)} />
                      <span className="flex-1 font-medium">{p.name}</span>
                      <span className="text-[10px] text-emerald-700 font-mono">{p.commission_percentage}%</span>
                    </label>
                  ))}
                  {personel.filter(p => p.active).length === 0 && (
                    <div className="p-3 text-center text-zinc-400 text-[11px]">Belum ada personel aktif</div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveAssignment} disabled={assignSaving}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 rounded-sm gap-1.5"
                  data-testid="assign-save-btn">
                  <Save className="w-3.5 h-3.5" /> Simpan
                </Button>
                <Button variant="outline" onClick={() => setAssignOpen(false)} className="rounded-sm">
                  Batal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v83.2 — Manual Billing modal */}
      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" onClick={() => setManualOpen(false)}>
          <div className="bg-white rounded-sm border border-zinc-200 shadow-lg w-full max-w-3xl p-5 my-8"
            onClick={e => e.stopPropagation()}
            data-testid="manual-billing-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-semibold text-[15px]">Billing Manual</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Catat transaksi billing yang tidak tergenerate otomatis dari WO.</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setManualOpen(false)} className="h-7 w-7">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Tanggal *</label>
                <Input type="date" value={manual.date}
                  onChange={e => setManual(m => ({ ...m, date: e.target.value }))}
                  className="h-9 rounded-sm text-[12px] font-mono"
                  data-testid="mb-date-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Armada *</label>
                <select value={manual.technician_id}
                  onChange={e => setManual(m => ({ ...m, technician_id: e.target.value }))}
                  className="h-9 w-full border border-zinc-200 rounded-sm bg-white px-2 text-[12px]"
                  data-testid="mb-armada-select">
                  <option value="">— pilih armada —</option>
                  {(techs || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Nama Customer *</label>
                <Input value={manual.customer_name}
                  onChange={e => setManual(m => ({ ...m, customer_name: e.target.value }))}
                  placeholder="Nama lengkap"
                  className="h-9 rounded-sm text-[12px]"
                  data-testid="mb-name-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
                  Nomor WhatsApp <span className="text-zinc-400 normal-case">(opsional — utk kirim invoice)</span>
                </label>
                <Input value={manual.customer_phone}
                  onChange={e => setManual(m => ({ ...m, customer_phone: e.target.value }))}
                  placeholder="08xxxxxxxx"
                  className="h-9 rounded-sm text-[12px] font-mono"
                  data-testid="mb-phone-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Alamat</label>
                <Input value={manual.address}
                  onChange={e => setManual(m => ({ ...m, address: e.target.value }))}
                  placeholder="Alamat lengkap customer"
                  className="h-9 rounded-sm text-[12px]"
                  data-testid="mb-address-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Keterangan Pekerjaan</label>
                <textarea value={manual.notes}
                  onChange={e => setManual(m => ({ ...m, notes: e.target.value }))}
                  placeholder="Mis. Servis AC 1PK cuci + isi freon"
                  rows={2}
                  className="w-full rounded-sm text-[12px] px-2 py-1.5 border border-zinc-200 resize-none"
                  data-testid="mb-notes-input" />
              </div>
            </div>

            {/* Items */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Rincian Item Tagihan *
                </label>
                <Button size="sm" variant="outline" onClick={addItem}
                  className="h-7 rounded-sm gap-1 text-[11px]"
                  data-testid="mb-add-item-btn">
                  <Plus className="w-3 h-3" /> Item
                </Button>
              </div>
              <div className="border border-zinc-200 rounded-sm">
                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-200">
                  <div className="col-span-6">Nama Item</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-3 text-right">Harga</div>
                  <div className="col-span-1"></div>
                </div>
                {manual.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-zinc-100 last:border-b-0"
                    data-testid={`mb-item-row-${i}`}>
                    <Input value={it.name}
                      onChange={e => updateItem(i, { name: e.target.value })}
                      placeholder="Mis. Cleaning AC 1PK"
                      className="col-span-6 h-8 rounded-sm text-[12px]"
                      data-testid={`mb-item-name-${i}`} />
                    <Input type="number" min="0" step="0.5" value={it.qty}
                      onChange={e => updateItem(i, { qty: e.target.value })}
                      className="col-span-2 h-8 rounded-sm text-[12px] font-mono text-right"
                      data-testid={`mb-item-qty-${i}`} />
                    <Input type="number" min="0" step="1000" value={it.price}
                      onChange={e => updateItem(i, { price: e.target.value })}
                      className="col-span-3 h-8 rounded-sm text-[12px] font-mono text-right"
                      data-testid={`mb-item-price-${i}`} />
                    <button
                      onClick={() => removeItem(i)}
                      disabled={manual.items.length <= 1}
                      className="col-span-1 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed rounded-sm"
                      data-testid={`mb-item-del-${i}`}
                      title="Hapus item">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between px-2 py-2 bg-orange-50 border-t border-orange-200">
                  <span className="text-[11px] uppercase tracking-widest text-[#E65100] font-semibold">Grand Total</span>
                  <span className="font-mono text-[14px] font-bold text-[#E65100]" data-testid="mb-grand-total">
                    {rp(manualGrand)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment + Send WA */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Metode Bayar</label>
                <div className="flex gap-1 border border-zinc-200 rounded-sm p-1 bg-white">
                  {["CASH","TRANSFER","QRIS"].map(pm => (
                    <button key={pm}
                      onClick={() => setManual(m => ({ ...m, payment_method: pm }))}
                      className={`flex-1 h-7 rounded-sm text-[11px] font-semibold transition-colors ${
                        manual.payment_method === pm
                          ? (pm === "CASH" ? "bg-emerald-600 text-white"
                             : pm === "TRANSFER" ? "bg-blue-600 text-white"
                             : "bg-purple-600 text-white")
                          : "text-zinc-600 hover:bg-zinc-50"
                      }`}
                      data-testid={`mb-pm-${pm}`}>
                      {pm}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-[12px] text-zinc-700 border border-zinc-200 rounded-sm px-3 h-9 flex-1 cursor-pointer hover:bg-zinc-50">
                  <input type="checkbox" checked={manual.send_wa}
                    onChange={e => setManual(m => ({ ...m, send_wa: e.target.checked }))}
                    disabled={!manual.customer_phone}
                    data-testid="mb-send-wa-checkbox" />
                  <span className={manual.customer_phone ? "" : "opacity-50"}>
                    Kirim invoice via WhatsApp {!manual.customer_phone && "(isi nomor dulu)"}
                  </span>
                </label>
              </div>
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setManualOpen(false)}
                className="h-9 rounded-sm" data-testid="mb-cancel-btn">
                Batal
              </Button>
              <Button onClick={saveManual} disabled={manualSaving}
                className="h-9 rounded-sm gap-1.5 bg-[#E65100] hover:bg-[#F57C00]"
                data-testid="mb-save-btn">
                <Save className="w-3.5 h-3.5" /> {manualSaving ? "Menyimpan…" : "Simpan Billing"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Total */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border-2 border-[#E65100] bg-orange-50 rounded-sm p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#E65100] font-semibold">
            <TrendingUp className="w-3.5 h-3.5"/> Total Tagihan
          </div>
          <div className="text-2xl font-bold text-[#E65100] mt-1 font-mono" data-testid="grand-total">
            {rp(data?.grand_total || 0)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{data?.count || 0} invoice</div>
        </div>
        {/* v37 — Jasa Teknisi (auto-kalkulasi dari Item ID di nama item) */}
        <div className="border-2 border-blue-600 bg-blue-50 rounded-sm p-4" data-testid="jasa-teknisi-card">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-700 font-semibold">
            <Wrench className="w-3.5 h-3.5"/> Jasa Teknisi
          </div>
          <div className="text-2xl font-bold text-blue-700 mt-1 font-mono" data-testid="total-tech-commission">
            {rp(data?.total_tech_commission || 0)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            {techId === "all" ? "Semua armada" : (techs.find(t=>t.id===techId)?.name || "armada dipilih")}
          </div>
        </div>
        {Object.entries(summary || {}).map(([m, v]) => {
          const showSplit = (m === "TRANSFER" || m === "QRIS");
          return (
            <div key={m} className="border border-zinc-200 rounded-sm p-4 bg-white"
                 data-testid={`summary-card-${m.toLowerCase()}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{m}</div>
                  <div className="text-xl font-bold mt-1 font-mono">{rp(v.total)}</div>
                </div>
                {showSplit && (
                  <div className="flex flex-col gap-1 min-w-[140px]"
                       data-testid={`summary-split-${m.toLowerCase()}`}>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="bg-green-600 text-white font-bold px-2 py-0.5 rounded whitespace-nowrap">
                        Paid
                      </span>
                      <span className="font-mono font-semibold text-green-700"
                            data-testid={`summary-paid-${m.toLowerCase()}`}>
                        {rp(v.paid)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="bg-amber-600 text-white font-bold px-2 py-0.5 rounded whitespace-nowrap">
                        Pending
                      </span>
                      <span className="font-mono font-semibold text-amber-700"
                            data-testid={`summary-pending-${m.toLowerCase()}`}>
                        {rp(v.pending)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* v84.83 — Chip filter status (client-side) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="status-filter-chips">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mr-1">
          Filter Status:
        </span>
        {[
          { key: "all",     label: "Semua",   count: statusCounts.all,     bg: "bg-zinc-700",   bgOff: "bg-zinc-100 text-zinc-700 hover:bg-zinc-200" },
          { key: "paid",    label: "Paid",    count: statusCounts.paid,    bg: "bg-green-600",  bgOff: "bg-green-50 text-green-700 hover:bg-green-100" },
          { key: "pending", label: "Pending", count: statusCounts.pending, bg: "bg-amber-600",  bgOff: "bg-amber-50 text-amber-700 hover:bg-amber-100" },
        ].map(c => (
          <button
            key={c.key}
            type="button"
            onClick={() => setStatusFilter(c.key)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded transition-colors ${
              statusFilter === c.key ? `${c.bg} text-white` : c.bgOff
            }`}
            data-testid={`status-filter-${c.key}`}>
            {c.label} <span className="opacity-70">({c.count})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <Table>
          <TableHeader className="bg-zinc-50">
            <TableRow>
              <TableHead className="w-28">Tanggal</TableHead>
              <TableHead>Armada</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Alamat</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right w-32" title="Auto-hitung dari Item ID di nama item (mis. LC162)">
                Jasa Teknisi
              </TableHead>
              {armadaLocked && (
                <TableHead className="w-40 text-center">Personel Bertugas</TableHead>
              )}
              <TableHead className="w-24 text-center">Bayar</TableHead>
              <TableHead className="w-28 text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!filteredRows || filteredRows.length === 0) && (
              <TableRow><TableCell colSpan={armadaLocked ? 9 : 8} className="text-center text-zinc-400 py-6">
                {loading ? "Memuat…"
                  : (statusFilter !== "all" && (data?.rows?.length || 0) > 0
                      ? `Tidak ada invoice dgn status ${statusFilter === "paid" ? "Paid" : "Pending"} pada filter ini`
                      : "Belum ada invoice pada filter ini")}
              </TableCell></TableRow>
            )}
            {filteredRows.map((r) => {
              const asg = armadaLocked ? assignmentForRowDate(r.date) : null;
              const names = asg ? personnelNamesFromIds(asg.personnel_ids) : [];
              return (
              <TableRow key={r.id} data-testid={`rekap-row-${r.id}`}
                className="hover:bg-orange-50/60 transition-colors">
                <TableCell className="text-[11px] font-mono cursor-pointer"
                  onClick={() => nav(`/billing-detail/${r.id}`)}>{r.date}</TableCell>
                <TableCell className="font-semibold text-[12px] cursor-pointer"
                  onClick={() => nav(`/billing-detail/${r.id}`)}>{r.technician_name}</TableCell>
                <TableCell className="text-[12px] cursor-pointer"
                  onClick={() => nav(`/billing-detail/${r.id}`)}>{r.customer_name || "—"}</TableCell>
                <TableCell className="text-[11px] text-zinc-600 max-w-md cursor-pointer"
                  onClick={() => nav(`/billing-detail/${r.id}`)}>
                  <div className="truncate" title={r.address}>{r.address || "—"}</div>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-[12px] cursor-pointer"
                  onClick={() => nav(`/billing-detail/${r.id}`)}>{rp(r.grand_total)}</TableCell>
                <TableCell className="text-right font-mono text-[12px] text-blue-700 cursor-pointer"
                  data-testid={`row-commission-${r.id}`}
                  onClick={() => nav(`/billing-detail/${r.id}`)}>
                  {r.tech_commission > 0 ? rp(r.tech_commission) : <span className="text-zinc-400">—</span>}
                </TableCell>
                {armadaLocked && (
                  <TableCell className="text-center">
                    {names.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {names.map((n, i) => (
                          <Badge key={i} className="bg-emerald-100 text-emerald-700 rounded-sm text-[10px] px-1.5 py-0">{n}</Badge>
                        ))}
                      </div>
                    ) : (
                      <button onClick={() => openAssignForDate(r.date)}
                        className="text-[10px] text-emerald-700 hover:underline"
                        data-testid={`assign-inline-${r.date}`}>
                        + assign
                      </button>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-center cursor-pointer" onClick={() => nav(`/billing-detail/${r.id}`)}>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${
                    r.payment_method === "CASH" ? "bg-emerald-600" :
                    r.payment_method === "TRANSFER" ? "bg-blue-600" :
                    r.payment_method === "QRIS" ? "bg-purple-600" :
                    "bg-zinc-500"
                  }`} data-testid={`row-method-${r.id}`}>{r.payment_method}</span>
                </TableCell>
                <TableCell className="text-center">
                  <select
                    value={r.payment_status || "PENDING"}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      // Optimistic update
                      const oldStatus = r.payment_status;
                      setData(d => ({
                        ...d,
                        rows: d.rows.map(row => row.id === r.id
                          ? { ...row, payment_status: newStatus, payment_status_source: "manual" }
                          : row),
                      }));
                      try {
                        await api.billingEdit(r.id, { payment_status: newStatus });
                        toast.success(`Status → ${newStatus}`);
                      } catch (err) {
                        // Rollback
                        setData(d => ({
                          ...d,
                          rows: d.rows.map(row => row.id === r.id
                            ? { ...row, payment_status: oldStatus }
                            : row),
                        }));
                        toast.error("Gagal update status: " + (err?.response?.data?.detail || err.message));
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded border-0 cursor-pointer text-white ${
                      (r.payment_status || "PENDING") === "PAID"
                        ? "bg-green-600"
                        : "bg-amber-600"
                    }`}
                    data-testid={`row-status-${r.id}`}>
                    <option value="PAID" className="bg-white text-black">Paid</option>
                    <option value="PENDING" className="bg-white text-black">Pending</option>
                  </select>
                </TableCell>
              </TableRow>
            );})}
            {data?.rows?.length > 0 && (
              <TableRow className="bg-orange-50 font-semibold">
                <TableCell colSpan={4} className="text-right text-[11px] uppercase tracking-widest text-[#E65100]">
                  Total ({data.count} invoice)
                </TableCell>
                <TableCell className="text-right font-mono text-[13px] text-[#E65100]" data-testid="table-total">
                  {rp(data.grand_total)}
                </TableCell>
                <TableCell className="text-right font-mono text-[13px] text-blue-700" data-testid="table-total-commission">
                  {rp(data.total_tech_commission || 0)}
                </TableCell>
                {armadaLocked && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
